import {
  AccessAvailability,
  AccessFrequency,
  AccessHours,
  AccessPermission,
  CardType,
  CheckoutType,
  CustomerType,
  Features,
  IGoogleAutocompleteSuggestion,
  IndoorOutdoor,
  Interest,
  LocationType,
  MessageEventType,
  OneTimeFeeCategory,
  PropertyType,
  SizeFilter,
  StorageTime,
  UseOfSpace,
  UserLocationsFilter,
  VehicleType
} from '../types/graphql'

export const DEFAULT_CENTER = { lat: 25.682, lng: -100.315 }

export const CENTER_MAP: { [key in string]: { lat: number; lng: number } } = {
  ['mty']: { lat: 25.682, lng: -100.315 },
  ['cdmx']: { lat: 19.4326077, lng: -99.133208 }
}

export const DEFAULT_ZOOM = 11
export const MIN_ZOOM = 9
/**
 * Zoom-in cap for every map that renders PUBLIC listing pins.
 *
 * `Location.coordinates` reaches non-owner viewers as a deterministic
 * ~80–200m decoy (server-side `obfuscateCoordinates`). At zoom 16+ that
 * offset resolves against street-level detail, so the decoy starts reading
 * as a precise address instead of an approximation. Capping at 15 keeps the
 * pin honestly vague.
 *
 * This is a UX guardrail, NOT the privacy boundary — the real protection is
 * the server-side offset. Do NOT apply it to host-facing pin placement
 * (AddPostFlow / EditListing), where the host is setting their OWN true
 * coordinates and needs deep zoom to drop the marker accurately.
 */
export const MAX_PUBLIC_PIN_ZOOM = 15
export const DEFAULT_PRICE_RANGE: [number, number] = [0, 500]

export const NEAR_USER = 'NEAR_USER'
export const NEAR_USER_DESCRIPTION = 'SEARCH_MANAGER.NEAR_TO_ME'

// Generic paginated surfaces (messaging, admin lists, fiscal docs) — keep at
// 10 unless a surface explicitly opts out.
export const PAGE_SIZE = 10
export const RENTS_PAGE_SIZE = 100
// Used by SearchManager's idle-time top-up (city/hood pages) and by the
// /search route's per-page upper bound. Sets the ceiling on map markers
// shown for a given search center: dense markets (CDMX/MTY self-storage)
// have hundreds of listings, so this constant trades payload size for
// map density. 100 is the current compromise — bumping to 200+ would
// warrant a dedicated lightweight markers fragment (id + coords + price
// only) to keep payloads in line.
export const MAP_PAGE_SIZE = 100

// Listing-grid sizing. SSG_PAGE_SIZE === SEARCH_GRID_SIZE so the first
// paint already fills the visible target — no awkward "9 listings + 1
// on-demand card = 10 items, orphan in 3-col" flash before the idle-time
// top-up lands. 12 = 6×2 = 4×3 → both md (2-col) and 2xl (3-col) grids
// land on a clean last row at hydration time. With the on-demand card
// inserted, SearchList trims to 11 listings + 1 card = 12 items, also
// divisible by both grid widths. SSG payload at 12 hits stays well
// under Next's 128 kB threshold (each CardLocationFields hit is small
// — id, photos, default config, no host data).
export const SSG_PAGE_SIZE = 12
export const SEARCH_GRID_SIZE = 12

export const LOCATION_TYPE_MAP: {
  [key in LocationType]: {
    icon?: string
    deprecated?: boolean
  }
} = {
  [LocationType.Warehouse]: { icon: 'si-warehouse' },
  [LocationType.Parking]: { icon: 'si-stop' },
  [LocationType.Room]: { icon: 'si-bed' },
  [LocationType.Local]: { icon: 'si-retail-space' },
  [LocationType.Land]: { icon: 'si-grounds' },

  [LocationType.Yard]: { icon: 'si-my-rentals' },
  [LocationType.Terrace]: { icon: 'si-house-plus' },
  [LocationType.Shed]: { icon: 'si-house' },
  [LocationType.ShippingContainer]: { icon: 'si-container' },
  [LocationType.SelfStorageUnit]: { icon: 'si-mezzanine' },

  [LocationType.Closet]: { icon: 'si-entrance' },
  [LocationType.Basement]: { icon: 'si-basement' },
  [LocationType.Garage]: { icon: 'si-garage' },
  [LocationType.Attic]: { icon: 'si-attic' },
  [LocationType.Other]: {},
  [LocationType.BodegaCommercial]: { icon: 'si-warehouse' },
  [LocationType.BodegaIndustrial]: { icon: 'si-warehouse' },
  [LocationType.NaveIndustrial]: { icon: 'si-warehouse' },
  [LocationType.Office]: { icon: 'si-commercial' },

  // Deprecated
  [LocationType.House]: {
    icon: 'si-house',
    deprecated: true
  },
  [LocationType.MiniWinery]: {
    icon: 'si-container',
    deprecated: true
  },
  [LocationType.Penthouse]: {
    icon: 'si-attic',
    deprecated: true
  }
}

export const INDOOR_OUTDOOR_MAP = {
  [IndoorOutdoor.Indoor]: {
    label: 'Interior',
    description: 'Cuartos, bodegas, cocheras, etc',
    icon: 'si-house'
  },
  [IndoorOutdoor.Outdoor]: {
    label: 'Exterior',
    description: 'Patios, terrenos, estacionamientos, etc',
    icon: 'si-grounds'
  },
  [IndoorOutdoor.OutdoorUncovered]: {
    label: 'Exterior techado',
    description: 'Estacionamientos techados, etc.',
    icon: 'si-store'
  }
}

export const PROPERTY_TYPE_MAP: {
  [key in PropertyType]: { label: string; description: string; icon: string }
} = {
  [PropertyType.Residential]: {
    label: 'Residencial',
    description: 'Vivienda o propiedad personal',
    icon: 'si-residential'
  },
  [PropertyType.Commercial]: {
    label: 'Comercial / Empresa',
    description:
      'Propiedad comercial o de negocio (oficina, local, bodega, plaza, estacionamiento, etc.)',
    icon: 'si-commercial'
  },
  [PropertyType.Brokerage]: {
    label: 'Agencia Inmobiliaria',
    description: 'Ofrezco espacio de terceros y formo parte de una agencia',
    icon: 'si-house'
  },
  [PropertyType.IndividualBroker]: {
    label: 'Broker Independiente',
    description: 'Ofrezco espacio de terceros',
    icon: 'si-user-single'
  }
}

export const CAN_STORE_VEHICLE_MAP = {
  '0': {
    label: 'Sí',
    description: 'Sí puedo y estoy dispuesto a almacenar vehículos',
    icon: 'si-vehicle-entrance'
  },
  '1': {
    label: 'No',
    description: 'No puedo almacenar vehículos',
    icon: 'si-box'
  }
}

export const DISCOUNT_MAP = {
  '0': {
    label: 'Sí, ofrezco descuento del 50% el primer mes',
    // Must match checkout: `discountMeetsMinimum(storageTime, 6)` applies the
    // coupon only to 6+ month stays, and the host's first payout is halved
    // (Stripe transfer on the discounted invoice). Corrected 2026-09-30 — it
    // said "mínimo dos meses" and never mentioned who pays.
    description:
      'Solo para rentas de 6 meses o más. Recibes la mitad del pago del primer mes; desde el segundo cobras completo.',
    icon: 'si-offert'
  },
  '1': {
    label: 'No, mantenlo a precio completo',
    icon: 'si-thumbs-down'
  }
}

export const ACCESS_PERMISSION_MAP: { [key in AccessPermission]: string } = {
  [AccessPermission.Notice]: 'Sí (con cita previa)',
  [AccessPermission.NoNotice]: 'No (sin cita previa)'
}

export const ACCESS_FREQUENCY_MAP: { [key in AccessFrequency]: string } = {
  [AccessFrequency.MonToFri]: 'Solo Lunes a Viernes',
  [AccessFrequency.MonToSat]: 'Solo Lunes a Sábado',
  [AccessFrequency.FriToSun]: 'Solo Viernes a Domingo',
  [AccessFrequency.Weekends]: 'Solo Sábado y Domingo',
  [AccessFrequency.Anyday]: 'Todos los días de la semana'
}

export const ACCESS_HOURS_MAP: { [key in AccessHours]: string } = {
  [AccessHours.Anytime]: 'Cualquier hora',
  [AccessHours.DaytimeHours]: 'Horario laboral (9am - 6pm)',
  [AccessHours.ExtendedHours]: 'Durante el dia (8am - 8pm)',
  [AccessHours.EveningHours]: 'Tardes (6pm en adelante)'
}

export const ACCESS_AVAILABILITY_MAP: { [key in AccessAvailability]: string } =
  {
    [AccessAvailability.Daily]: 'Diariamente',
    [AccessAvailability.Weekly]: 'Semanalmente (algunas veces / semana)',
    [AccessAvailability.Monthly]: 'Mensualmente (algunas veces / mes)',
    [AccessAvailability.Unfrequently]:
      'Con poca frecuencia (menos de una vez / mes)'
  }

export const FEATURES_MAP: {
  [key in Features]: {
    label: string
    icon?: string
    description?: string
    deprecated?: boolean
  }
} = {
  [Features.PrivateEntrance]: {
    label: 'Entrada independiente',
    icon: 'si-entrance'
  },
  [Features.PrivateSpace]: { label: 'Espacio privado', icon: 'si-privacy' },
  [Features.SecurityCamera]: {
    label: 'Cámara de seguridad',
    icon: 'si-camera'
  },
  [Features.FloorLevel]: {
    label: 'A nivel del piso',
    icon: 'si-entrance'
  },
  [Features.PetFree]: {
    label: 'Libre de mascotas',
    icon: 'si-close'
  },

  [Features.SmokeDetectors]: {
    label: 'Detector de humo',
    icon: 'si-fire-alarm'
  },
  [Features.Fumigated]: {
    label: 'Fumigado',
    icon: 'si-house-shield'
  },
  [Features.ClimateControl]: {
    label: 'Control de temperatura',
    icon: 'si-temperature-control'
  },
  [Features.Surveillance]: {
    label: 'Vigilancia',
    icon: 'si-house-shield'
  },
  [Features.VehicleAccess]: {
    label: 'Acceso Vehicular',
    icon: 'si-vehicle-entrance'
  },

  [Features.Elevator]: { label: 'Elevador', icon: 'si-elevator' },
  [Features.AlarmSystem]: { label: 'Sistema de alarma', icon: 'si-alarms' },
  [Features.AntiFireSystem]: {
    label: 'Sistema contra incendio',
    icon: 'si-fire-alarm'
  },
  [Features.Mezzanine]: { label: 'Mezzanine', icon: 'si-mezzanine' },
  [Features.Platform]: { label: 'Andén', icon: 'si-mezzanine' },
  [Features.Water]: { label: 'Servicio de agua y drenaje', icon: 'si-water' },

  [Features.TruckOrTrailerAccess]: {
    label: 'Acceso a camiones/tráilers',
    icon: 'si-delivery-truck'
  },
  [Features.Railyard]: { label: 'Patio de maniobras', icon: 'si-signal' },
  [Features.Gas]: { label: 'Gas', icon: 'si-gas' },
  [Features.Toilets]: { label: 'Baños', icon: 'si-bathroom' },
  [Features.ElectricalInstallation]: {
    label: 'Instalación eléctrica (luz, contactos, interruptores)',
    icon: 'si-light'
  },

  [Features.Shelves]: {
    label: 'Estantes/Racks/Bastidores',
    icon: 'si-mezzanine'
  },
  [Features.Reception]: { label: 'Recepcionista', icon: 'si-user' },
  [Features.Offices]: { label: 'Oficinas', icon: 'si-identificator' },
  [Features.Cafeteria]: { label: 'Comedor', icon: 'si-store' },
  [Features.Tank]: { label: 'Cisterna', icon: 'si-cistern' },

  [Features.LockedArea]: { label: 'Candado', icon: 'si-lock' },
  [Features.CodeAccess]: { label: 'Código de acceso', icon: 'si-lock' },
  [Features.KeyAccess]: { label: 'Acceso con llave', icon: 'si-lock' },
  [Features.Waterproofed]: { label: 'Impermeabilizado', icon: 'si-water' },
  [Features.Tinaco]: { label: 'Tinaco', icon: 'si-cistern' },
  [Features.Stairs]: { label: 'Escaleras', icon: 'si-stairs' },
  [Features.ArmoredSpace]: { label: 'Espacio blindado', icon: 'si-lock' },

  // Deprecated
  [Features.Light]: {
    label: 'Instalación eléctrica',
    icon: 'si-light',
    deprecated: true
  },
  [Features.MeetingRooms]: {
    label: 'Sala de juntas',
    icon: 'si-meeting-room',
    deprecated: true
  },
  [Features.AirConditioner]: {
    label: 'Aire acondicionado',
    icon: 'si-air-conditioning',
    deprecated: true
  },
  [Features.ClassificationYard]: {
    label: 'Patio de maniobras',
    icon: 'si-signal',
    deprecated: true
  },
  [Features.IndependentEntrance]: {
    label: 'Entrada Independiente',
    icon: 'si-entrance',
    deprecated: true
  },
  [Features.PestControl]: {
    label: 'Control de plagas',
    icon: 'si-gas',
    deprecated: true
  },
  [Features.Bathrooms]: {
    label: 'Baños',
    icon: 'si-bathroom',
    deprecated: true
  },
  [Features.ShieldedSpace]: {
    label: 'Espacio techado',
    icon: 'si-store',
    deprecated: true
  }
}

const EXCLUDED = [
  Features.SmokeDetectors,
  Features.Tinaco,
  Features.ArmoredSpace
]
export const FEATURES_MAP_EXCL = Object.entries(FEATURES_MAP)
  .filter(([key]) => !EXCLUDED.includes(key as Features))
  .reduce(
    (
      acc: { [key: string]: typeof FEATURES_MAP.AIR_CONDITIONER },
      [key, value]
    ) => {
      acc[key] = value
      return acc
    },
    {}
  )

export const FEATURES_OPTIONS = Object.entries(FEATURES_MAP)
  .filter(([key]) => !EXCLUDED.includes(key as Features))
  .map(([key, value]): { value: Features; label: string } => ({
    value: key as Features,
    label: value.label
  }))

export const USE_OF_SPACE_MAP: { [key in UseOfSpace]: string } = {
  [UseOfSpace.None]: 'No sé',
  [UseOfSpace.Commercial]: 'Comercial',
  [UseOfSpace.Residencial]: 'Habitacional',
  [UseOfSpace.Industrial]: 'Industrial',
  [UseOfSpace.Mixed]: 'Mixto'
}

export const STORAGE_TIME_MAP: { [key in StorageTime]: string } = {
  [StorageTime.Month]: 'Un mes',
  [StorageTime.FewMonths]: 'Un par de meses (1-3 meses)',
  [StorageTime.FewMonthsSix]: 'Unos meses (3-5 meses)',
  [StorageTime.FewMonthsTwelve]: '6 meses a 1 año',
  [StorageTime.Year]: 'Un año',
  [StorageTime.FewYears]: 'Un par de años (1-3 años)',
  [StorageTime.Indefinitely]: 'Indefinido'
}

export const CARD_TYPE_MAP: { [key in CardType]: string } = {
  [CardType.Credit]: 'Credito',
  [CardType.Debit]: 'Debito'
}
export const VEHICLE_TYPE_MAP: { [key in VehicleType]: string } = {
  [VehicleType.Car]: 'Coche',
  [VehicleType.Boat]: 'Bote',
  [VehicleType.Motorcycle]: 'Motocicleta'
}

export const SERVICE_FEE = 19.69

export const MAX_NUMBER_OF_UPLOADS = 15

export const NEAR_USER_OPTION: IGoogleAutocompleteSuggestion = {
  __typename: 'GoogleAutocompleteSuggestion',
  placeId: NEAR_USER,
  description: NEAR_USER_DESCRIPTION,
  friendlyDisplay: NEAR_USER_DESCRIPTION
}

export const MTY_OPTION: IGoogleAutocompleteSuggestion = {
  __typename: 'GoogleAutocompleteSuggestion',
  placeId: 'ChIJ9fg3tDGVYoYRlJjIasrT06M',
  description: 'Monterrey, N.L.',
  friendlyDisplay: 'Monterrey, N.L.'
}

export const CDMX_OPTION: IGoogleAutocompleteSuggestion = {
  __typename: 'GoogleAutocompleteSuggestion',
  placeId: 'ChIJB3UJ2yYAzoURQeheJnYQBlQ',
  description: 'Ciudad de México, CDMX',
  friendlyDisplay: 'Ciudad de México, CDMX'
}

export const CHECKOUT_STATUS_MAP: { [tab in CheckoutType]: string } = {
  [CheckoutType.Pending]: 'Pendiente',
  [CheckoutType.Active]: 'Activa',
  [CheckoutType.Past]: 'Anterior',
  [CheckoutType.UpComing]: 'Próximo',
  [CheckoutType.Expired]: 'Vencida'
}

export const CHECKOUT_COLOR_MAP: { [tab in CheckoutType]: string } = {
  [CheckoutType.Pending]: 'bg-yellow-500',
  [CheckoutType.Active]: 'bg-blue-500',
  [CheckoutType.Past]: 'bg-black-400',
  [CheckoutType.UpComing]: 'bg-green-500',
  [CheckoutType.Expired]: 'bg-orange-500'
}

export const CHECKOUT_TAB_MAP: { [tab in CheckoutType]: string } = {
  [CheckoutType.Pending]: 'Pendientes',
  [CheckoutType.Active]: 'Activas',
  [CheckoutType.Past]: 'Anteriores',
  [CheckoutType.UpComing]: 'Próximo',
  [CheckoutType.Expired]: 'Vencidas'
}

export const USER_LOCATIONS_TYPE_MAP: { [tab in UserLocationsFilter]: string } =
  {
    [UserLocationsFilter.All]: 'Todos',
    [UserLocationsFilter.Pending]: 'Pendientes',
    [UserLocationsFilter.Active]: 'Activos',
    [UserLocationsFilter.Inactive]: 'Inactivos',
    [UserLocationsFilter.Drafts]: 'Borradores'
  }

export const TOPBAR_HEIGHT_MD = 72
export const TOPBAR_HEIGHT_SM = 56

export const DEFAULT_FILTERS = {
  canStoreVehicle: undefined,
  size: SizeFilter.Any,
  priceRange: DEFAULT_PRICE_RANGE,
  zoom: DEFAULT_ZOOM
}

export const INTEREST_MAP: {
  [key in Interest]: { label: string; description: string; icon: string }
} = {
  [Interest.Rent]: {
    label: 'Rentero',
    description: 'Encuentra espacio',
    icon: 'si-location'
  },
  [Interest.Host]: {
    label: 'Anfitrión',
    description: 'Ofrece almacenamiento',
    icon: 'si-house'
  }
}

export const CUSTOMER_TYPE_MAP: {
  [key in CustomerType]?: { label: string; description: string; icon: string }
} = {
  [CustomerType.RentIndividual]: {
    label: 'Rentero',
    description: 'Encuentra espacio',
    icon: 'si-location-dot'
  },
  [CustomerType.HostIndividual]: {
    label: 'Anfitrión',
    description: 'Ofrece tu espacio',
    icon: 'si-house'
  },
  [CustomerType.HostCorporate]: {
    label: 'Corporativo',
    description: 'Ofrece espacio de tu empresa',
    icon: 'si-warehouse'
  },
  [CustomerType.HostBroker]: {
    label: 'Broker',
    description: 'Ofrece espacio de terceros',
    icon: 'si-user-single'
  }
}

export const MESSAGE_EVENT_TYPE_MAP: { [key in MessageEventType]: string } = {
  [MessageEventType.RequestSent]: 'Solicitud enviada',
  [MessageEventType.RequestAccepted]: 'Solicitud aceptada',
  [MessageEventType.RequestRejected]: 'Solicitud rechazada',
  [MessageEventType.CheckoutStart]: 'Reserva iniciada',
  [MessageEventType.CheckoutCancelled]: 'Reserva cancelada',
  [MessageEventType.CheckoutEnded]: 'Reserva finalizada'
}

export const ONE_TIME_FEE_CATEGORY_LABELS: Record<OneTimeFeeCategory, string> =
  {
    [OneTimeFeeCategory.SecurityDeposit]: 'Depósito en Garantía',
    [OneTimeFeeCategory.AccessCard]: 'Tarjeta de acceso',
    [OneTimeFeeCategory.Other]: 'Otro'
  }
